import { forwardRef, useRef, useState } from "react";
import { Check, ChevronDown, ChevronLeft, ChevronRight, RefreshCcw, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const CONFIG_TYPES = [
  { value: "CURTAIN", label: "Curtain" },
  { value: "ROMAN_BLIND", label: "Roman Blind" },
  { value: "BLIND", label: "Blind" },
];
const STYLE_OPTIONS = ["Pleat", "Eyelet", "Panel"];
const MOUNTING_OPTIONS = ["Ceiling", "Wall"];

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "min-w-[6.5rem] px-4 py-2 rounded-xl text-sm border transition-colors",
        active ? "bg-accent/10 text-accent border-accent/60 font-medium" : "bg-card text-foreground/80 border-border hover:border-foreground/30",
      )}
    >
      {children}
    </button>
  );
}

const Step = forwardRef(function Step({ n, title, aside, children, className }, ref) {
  return (
    <div ref={ref} className={cn("px-5 py-4 scroll-mt-24", className)}>
      <div className="flex items-center gap-2.5 mb-3">
        <span className="h-6 w-6 rounded-full bg-accent text-accent-foreground text-xs font-semibold flex items-center justify-center">{n}</span>
        <h3 className="text-[15px] font-medium text-foreground flex-1">{title}</h3>
        {aside}
      </div>
      {children}
    </div>
  );
});

function FabricStrip({ products, loading, selectedId, onSelect, emptyText }) {
  const scroller = useRef(null);
  const scroll = (dir) => scroller.current?.scrollBy({ left: dir * 260, behavior: "smooth" });
  return (
    <div className="relative">
      {loading && <p className="sr-only" data-testid="fabrics-loading">Loading fabrics…</p>}
      <div ref={scroller} className="flex gap-3 overflow-x-auto pb-2 snap-x scroll-smooth [scrollbar-width:thin]">
        {loading &&
          Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="w-[5.5rem] shrink-0" data-testid="fabric-skeleton">
              <Skeleton className="h-[4.5rem] rounded-xl bg-secondary" />
              <Skeleton className="h-3 w-3/4 mt-2 bg-secondary" />
            </div>
          ))}
        {!loading &&
          products.map((p) => {
            const selected = selectedId === p.id;
            return (
              <button
                key={p.id}
                type="button"
                data-testid={`select-product-card-${p.id}`}
                aria-pressed={selected}
                onClick={() => onSelect(p)}
                className="group w-[5.5rem] shrink-0 snap-start text-left"
                title={p.name}
              >
                <span
                  className={cn(
                    "relative block h-[4.5rem] rounded-xl overflow-hidden border-2 bg-secondary transition-colors",
                    selected ? "border-accent" : "border-transparent group-hover:border-foreground/25",
                  )}
                >
                  {p.imageUrls?.[0] && <img src={p.imageUrls[0]} alt="" loading="lazy" className="h-full w-full object-cover" />}
                  {selected && (
                    <span className="absolute top-1 right-1 h-5 w-5 rounded-full bg-accent text-accent-foreground flex items-center justify-center shadow">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                  )}
                </span>
                <span className="block mt-1.5 text-xs font-medium text-foreground truncate">{p.name}</span>
                <span className="block text-[11px] text-muted-foreground truncate">{p.colour || " "}</span>
              </button>
            );
          })}
        {!loading && products.length === 0 && <p className="text-sm text-muted-foreground py-4">{emptyText}</p>}
      </div>
      {!loading && products.length > 4 && (
        <>
          <button
            type="button"
            aria-label="Scroll fabrics left"
            onClick={() => scroll(-1)}
            className="hidden sm:flex absolute -left-2 top-7 h-8 w-8 rounded-full bg-card border border-border shadow items-center justify-center hover:bg-secondary"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Scroll fabrics right"
            onClick={() => scroll(1)}
            className="hidden sm:flex absolute -right-2 top-7 h-8 w-8 rounded-full bg-card border border-border shadow items-center justify-center hover:bg-secondary"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}

export default function DesignPanel({
  refs,
  configForm,
  setConfigForm,
  onTypeChange,
  catalogues,
  unapprovedCount,
  catalogueId,
  setCatalogueId,
  products,
  productsLoading,
  selectedProduct,
  onSelectProduct,
  suggestedPanels,
  onPanelsChange,
  onResetPanels,
  windowWidth,
  onGenerate,
  generateLabel,
  generateDisabled,
  generateHint,
  onTryAnother,
  canTryAnother,
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const typeLabel = CONFIG_TYPES.find((t) => t.value === configForm.configType)?.label.toLowerCase();

  return (
    <section className="bg-card border border-border rounded-2xl flex flex-col" data-testid="design-panel">
      <div className="divide-y divide-border flex-1">
        <Step n={1} title="Select Product" ref={refs.product}>
          <div className="flex flex-wrap gap-2">
            {CONFIG_TYPES.map((t) => (
              <Chip key={t.value} testId={`config-type-chip-${t.value}`} active={configForm.configType === t.value} onClick={() => onTypeChange(t.value)}>
                {t.label}
              </Chip>
            ))}
          </div>
        </Step>

        <Step n={2} title="Select Catalogue">
          <Select value={catalogueId} onValueChange={setCatalogueId}>
            <SelectTrigger data-testid="select-catalogue-dropdown" className="h-11 rounded-xl bg-card">
              <SelectValue placeholder="Choose catalogue" />
            </SelectTrigger>
            <SelectContent>
              {catalogues.map((c) => (
                <SelectItem key={c._id} value={c._id}>{c.companyName} — {c.versionName || c.catalogueReference}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {unapprovedCount > 0 && (
            <p className="text-xs text-muted-foreground mt-1.5" data-testid="unapproved-catalogues-hint">
              {unapprovedCount} catalogue{unapprovedCount === 1 ? "" : "s"} not approved by the platform {unapprovedCount === 1 ? "is" : "are"} hidden.
            </p>
          )}
        </Step>

        <Step
          n={3}
          title="Select Fabric"
          ref={refs.fabric}
          aside={
            selectedProduct && (
              <span className="text-[11px] font-mono-spec bg-secondary rounded px-2 py-0.5 text-muted-foreground" data-testid="selected-product-reference">
                Ref: {selectedProduct.shopReference}
              </span>
            )
          }
        >
          <FabricStrip
            products={catalogueId ? products : []}
            loading={!!catalogueId && productsLoading}
            selectedId={selectedProduct?.id}
            onSelect={onSelectProduct}
            emptyText={catalogueId ? `No ${typeLabel} fabrics in this catalogue.` : "Choose a catalogue to browse fabrics."}
          />
        </Step>

        <Step n={4} title="Select Style" ref={refs.style}>
          <div className="flex flex-wrap gap-2">
            {STYLE_OPTIONS.map((s) => (
              <Chip key={s} testId={`style-chip-${s}`} active={configForm.pleat === s} onClick={() => setConfigForm((f) => ({ ...f, pleat: s }))}>
                {s}
              </Chip>
            ))}
          </div>

          <button
            type="button"
            aria-expanded={moreOpen}
            data-testid="more-options-toggle"
            onClick={() => setMoreOpen((v) => !v)}
            className="mt-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            Mounting, {configForm.configType === "CURTAIN" ? "panels & " : ""}quantity
            <ChevronDown className={cn("h-4 w-4 transition-transform", moreOpen && "rotate-180")} />
          </button>
          {moreOpen && (
            <div className="mt-3 space-y-3 hi-animate-fade-up">
              <div className="flex flex-wrap gap-2">
                {MOUNTING_OPTIONS.map((m) => (
                  <Chip key={m} testId={`mounting-chip-${m}`} active={configForm.mounting === m} onClick={() => setConfigForm((f) => ({ ...f, mounting: m }))}>
                    {m} mount
                  </Chip>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 max-w-xs">
                {configForm.configType === "CURTAIN" && (
                  <div>
                    <Label className="text-xs">Panels</Label>
                    <Input data-testid="config-panels-input" type="number" min={1} value={configForm.panels ?? ""} onChange={(e) => onPanelsChange(e.target.value === "" ? null : Number(e.target.value))} />
                  </div>
                )}
                <div>
                  <Label className="text-xs">Quantity</Label>
                  <Input data-testid="config-quantity-input" type="number" min={1} value={configForm.quantity} onChange={(e) => setConfigForm((f) => ({ ...f, quantity: Number(e.target.value) }))} />
                </div>
              </div>
              {configForm.configType === "CURTAIN" && (
                <p className="text-xs text-muted-foreground" data-testid="panels-hint">
                  Panels are the fabric widths stitched together for the curtain.
                  {suggestedPanels && (
                    <>
                      {" "}Suggested {suggestedPanels} for a {windowWidth}″ window.
                      {configForm.panels !== suggestedPanels && (
                        <button type="button" data-testid="reset-panels-button" className="ml-1 text-accent hover:underline" onClick={onResetPanels}>
                          Reset to {suggestedPanels}
                        </button>
                      )}
                    </>
                  )}
                </p>
              )}
            </div>
          )}
        </Step>
      </div>

      <div className="px-5 pb-5 pt-1">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            data-testid="generate-ai-preview-button"
            disabled={generateDisabled}
            onClick={onGenerate}
            className="h-11 bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            <Sparkles className="h-4 w-4 mr-2" /> {generateLabel}
          </Button>
          <Button variant="outline" data-testid="try-another-fabric-button" disabled={!canTryAnother} onClick={onTryAnother} className="h-11">
            <RefreshCcw className="h-4 w-4 mr-2" /> Try Another Fabric
          </Button>
        </div>
        {generateHint && <p className="text-xs text-muted-foreground mt-2" data-testid="generate-hint">{generateHint}</p>}
      </div>
    </section>
  );
}
